import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput, formatEmailTime } from "./shared/utils";

interface Spreadsheet {
  id: string;
  name?: string;
  title?: string;
  modifiedTime?: string;
  webViewLink?: string;
  url?: string;
}

interface ParsedSheetsList {
  ok: true;
  files?: Spreadsheet[];
  spreadsheets?: Spreadsheet[];
}

interface SheetsListCardProps {
  part: ToolUIPart;
}

export const SheetsListCard = memo(function SheetsListCard({ part }: SheetsListCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<ParsedSheetsList>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const sheets = result.kind === "ok"
    ? (result.data.files ?? result.data.spreadsheets ?? [])
    : [];

  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const badge = isStreaming ? undefined : isError ? "error" : `${sheets.length} spreadsheet${sheets.length !== 1 ? "s" : ""}`;

  return (
    <CardShell
      icon="⊞"
      title="Spreadsheets"
      badge={badge}
      accent={isError ? "error" : "sheets"}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__loading">Loading spreadsheets...</div>
      )}
      {isError && (
        <div className="tool-card__error-msg">{result.kind === "error" ? result.error : "Error"}</div>
      )}
      {!isStreaming && !isError && sheets.length === 0 && (
        <div className="tool-card__empty">No spreadsheets found.</div>
      )}
      {!isStreaming && !isError && sheets.length > 0 && (
        <div role="list">
          {sheets.map((sheet, i) => {
            const name = sheet.name ?? sheet.title ?? "(untitled)";
            const link = sheet.webViewLink ?? sheet.url;
            const modified = sheet.modifiedTime ? formatEmailTime(sheet.modifiedTime) : "";

            return (
              <div key={sheet.id ?? i} className="tool-card__doc-row" role="listitem">
                <span className="tool-card__doc-icon" aria-hidden="true">⊞</span>
                <span className="tool-card__doc-details">
                  {link ? (
                    <a
                      className="tool-card__doc-name"
                      href={link}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`${name} (opens in new tab)`}
                    >
                      {name}
                    </a>
                  ) : (
                    <span className="tool-card__doc-name">{name}</span>
                  )}
                </span>
                {modified && (
                  <span className="tool-card__doc-time">{modified}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
});
